// src/components/common/Feedback/Feedbackform.jsx

import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { createFeedback } from '../../../services/platformFeedbackService';

import {
  Box, Paper, Typography, Button, TextField,
  FormControl, InputLabel, Select, MenuItem,
  IconButton, Snackbar, Alert, Stack, Divider,
  CircularProgress, Fade, Dialog, DialogContent,
} from '@mui/material';

import StarIcon         from '@mui/icons-material/Star';
import StarBorderIcon   from '@mui/icons-material/StarBorder';
import CloseIcon        from '@mui/icons-material/Close';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import SendIcon         from '@mui/icons-material/Send';

// ─── Constants ────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { value: 'general',     label: 'General Experience'      },
  { value: 'ui-ux',       label: 'User Interface'          },
  { value: 'performance', label: 'System Performance'      },
  { value: 'features',    label: 'Features & Functionality'},
  { value: 'support',     label: 'Customer Support'        },
  { value: 'bug',         label: 'Bug Report'              },
];

const RATING_LABELS = {
  1: 'Poor',
  2: 'Fair',
  3: 'Good',
  4: 'Very Good',
  5: 'Excellent',
};

const MAX_COMMENT_LENGTH = 500;

// ─── Star Rating Component ────────────────────────────────────────────────────

const StarRating = ({ rating, onRatingChange, disabled }) => {
  const [hoverRating, setHoverRating] = useState(0);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {[1, 2, 3, 4, 5].map((star) => {
          const isFilled = star <= (hoverRating || rating);
          return (
            <IconButton
              key={star}
              onClick={() => !disabled && onRatingChange(star)}
              onMouseEnter={() => !disabled && setHoverRating(star)}
              onMouseLeave={() => !disabled && setHoverRating(0)}
              disabled={disabled}
              sx={{
                p: 0.5,
                color: isFilled ? '#f5a623' : '#ccc',
                transition: 'transform 0.1s',
                '&:hover': { transform: 'scale(1.15)' },
              }}
            >
              {isFilled
                ? <StarIcon sx={{ fontSize: 32 }} />
                : <StarBorderIcon sx={{ fontSize: 32 }} />
              }
            </IconButton>
          );
        })}
      </Box>
      {rating > 0 && (
        <Typography variant="body2" fontWeight={600} color="warning.main" sx={{ ml: 1 }}>
          {RATING_LABELS[rating]}
        </Typography>
      )}
    </Box>
  );
};

// ─── Success State ────────────────────────────────────────────────────────────

const SuccessMessage = () => (
  <Box sx={{
    textAlign: 'center', py: 6, px: 4,
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: 2,
  }}>
    <Box sx={{
      width: 80, height: 80, borderRadius: '50%',
      bgcolor: 'rgba(46,125,50,0.08)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
    </Box>
    <Typography variant="h5" fontWeight={700} color="text.primary">
      Thank You!
    </Typography>
    <Typography variant="body1" color="text.secondary">
      Your feedback has been submitted successfully.
    </Typography>
  </Box>
);

// ─── Main Form Component ──────────────────────────────────────────────────────

const Feedbackform = ({ onClose, showAsModal = false }) => {
  const { currentUser } = useAuth();

  // ── State ──────────────────────────────────────────────────────────────────
  const [rating,       setRating]       = useState(0);
  const [comment,      setComment]      = useState('');
  const [category,     setCategory]     = useState('general');
  const [loading,      setLoading]      = useState(false);
  const [submitted,    setSubmitted]    = useState(false);
  const [notification, setNotification] = useState(null);

  // ── Submit handler via service → repo ──────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rating === 0) {
      setNotification({ type: 'warning', message: 'Please provide a rating' });
      return;
    }

    setLoading(true);
    try {
     // Tolerate both Firebase-style (uid) and Supabase-style (id) auth objects
const userId    = currentUser?.uid ?? currentUser?.id ?? null;
const userEmail = currentUser?.email ?? null;

if (!userId || !userEmail) {
  setNotification({
    type: 'warning',
    message: 'Please sign in to submit feedback.',
  });
  setLoading(false);
  return;
}

const feedbackData = {
  userId,
  userName:  currentUser?.displayName || currentUser?.name || userEmail,
  userEmail,
  rating,
  comment:   comment.trim(),
  category,
};

await createFeedback(feedbackData, currentUser);

      setSubmitted(true);
      setNotification({ type: 'success', message: 'Feedback submitted successfully!' });

      // Reset form after 2s
      setTimeout(() => {
        setRating(0);
        setComment('');
        setCategory('general');
        setSubmitted(false);
        if (onClose) onClose();
      }, 2000);
    } catch (err) {
      console.error('[Feedbackform] submit error:', err);
      setNotification({
        type: 'error',
        message: 'Failed to submit feedback. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Form JSX ───────────────────────────────────────────────────────────────
  const formContent = (
    <Paper
      variant={showAsModal ? undefined : 'outlined'}
      sx={{
        borderRadius: 3,
        p: { xs: 3, md: 4 },
        boxShadow: showAsModal ? 'none' : '0 4px 16px rgba(0,0,0,0.02)',
      }}
    >
      {submitted ? (
        <Fade in={submitted}>
          <Box><SuccessMessage /></Box>
        </Fade>
      ) : (
        <>
          {/* Header inside form */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
            <Box>
              <Typography variant="h5" fontWeight={700} color="text.primary">
                Share Your Feedback
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Help us improve your experience
              </Typography>
            </Box>
            {showAsModal && (
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            )}
          </Stack>

          <Divider sx={{ mb: 3 }} />

          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>

              {/* ── Category ── */}
              <FormControl fullWidth size="small">
                <InputLabel>Feedback Category</InputLabel>
                <Select
                  value={category}
                  label="Feedback Category"
                  onChange={e => setCategory(e.target.value)}
                  disabled={loading}
                  sx={{ borderRadius: 2 }}
                >
                  {CATEGORIES.map(cat => (
                    <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* ── Rating ── */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
                  Overall Rating <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                </Typography>
                <StarRating
                  rating={rating}
                  onRatingChange={setRating}
                  disabled={loading}
                />
              </Box>

              {/* ── Comment ── */}
              <Box>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  size="small"
                  label="Comments (Optional)"
                  placeholder="Tell us more about your experience..."
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  disabled={loading}
                  inputProps={{ maxLength: MAX_COMMENT_LENGTH }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <Typography
                  variant="caption"
                  color={
                    comment.length >= MAX_COMMENT_LENGTH * 0.9
                      ? 'warning.main'
                      : 'text.secondary'
                  }
                  sx={{
                    display: 'block', mt: 0.5, textAlign: 'right', fontWeight: 500,
                  }}
                >
                  {comment.length}/{MAX_COMMENT_LENGTH} characters
                </Typography>
              </Box>

              {/* ── Submit Button ── */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, pt: 1 }}>
                {showAsModal && (
                  <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={loading}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading || rating === 0 || !currentUser}
                  disableElevation
                  startIcon={
                    loading
                      ? <CircularProgress size={16} color="inherit" />
                      : <SendIcon />
                  }
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    minWidth: 160,
                  }}
                >
                  {loading ? 'Submitting...' : 'Submit Feedback'}
                </Button>
              </Box>

            </Stack>
          </form>
        </>
      )}
    </Paper>
  );

  return (
    <>
      {/* Snackbar for validation + errors */}
      <Snackbar
        open={!!notification}
        autoHideDuration={4000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity={notification?.type || 'info'}
          onClose={() => setNotification(null)}
          sx={{ fontWeight: 500 }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>

      {/* Render as modal or inline */}
      {showAsModal ? (
        <Dialog
          open
          onClose={onClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogContent sx={{ p: 0 }}>{formContent}</DialogContent>
        </Dialog>
      ) : (
        formContent
      )}
    </>
  );
};

export default Feedbackform;